// @ts-strict-ignore
import { BaseContainer3D } from './base-container-3d';
import { ArticleCompositionVO } from '@article/model/vo/article.vo';
import type { ArticleContainer } from './article-container';
import { Object3DUtils } from '../common/util/object-3d-utils';
import { Box3, Object3D } from 'three';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleContainerUtils } from './util/article-container-utils';
import { SceneManager } from '../manager/scene.manager';
import { ImageTextureProcessor } from '../processor/image-texture.processor';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { ArticlesVisibilityService } from '@project/components/article/service/articles-visibility.service';
import { LabelStripArticleManager } from '../manager/label-strip-article.manager';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { ArticleMoveManager } from '@project/components/article/manager/article-move.manager';
import { CompositeArticleManager } from '@article/manager/composite-article.manager';
import { ArticleLoadingManager } from '@article/service/article-loading.manager';
import { BridgeArticleManager } from '@project/components/article/manager/bridge-article.manager';
import { ArticleContainerFactory } from './article-container.factory';
import { ArticleContainerAttachedService, AttachedArticleContainer } from '@project/components/article/service/article-container-attached.service';
import { AttachArticleUsageService } from '@project/service/attach-article-usage.service';
import { AttachCodingFingerArticleService } from '@project/components/article/service/attach-coding-finger-article.service';
import { DetachCodingFingerArticleService } from '@project/components/article/service/detach-coding-finger-article.service';
import { ArticleCompositionUtils } from '@project/util/article-composition-utils';
import { ContainerUtils } from './container.utils';

export class CompositionContainer extends BaseContainer3D {

  public composition: ArticleCompositionVO;

  constructor(
    private projectModel: ProjectModel,
    private articleMoveManager: ArticleMoveManager,
    private sceneManager: SceneManager,
    private projectContentPresenter: ProjectContentPresenter,
    private compositeArticleManager: CompositeArticleManager,
    private articleLoadingManager: ArticleLoadingManager,
    private articlesVisibilityService: ArticlesVisibilityService,
    private bridgeArticleManager: BridgeArticleManager,
    private carrierBlockManager: CarrierBlockManager,
    private attachCodingFingerArticleService: AttachCodingFingerArticleService,
    private detachCodingFingerArticleService: DetachCodingFingerArticleService,
    private labelStripArticleManager: LabelStripArticleManager,
    private imageTextureProcessor: ImageTextureProcessor,
    private articleContainerFactory: ArticleContainerFactory,
    private _articleContainerAttachedService: ArticleContainerAttachedService,
    private attachArticleUsageService: AttachArticleUsageService
  ) {
    super();
  }

  public readonly isCompositionContainer = true;

  public get compositionGuid(): string {
    return this.composition ? this.composition.id : null;
  }

  public updateProjectModel(): void {
    this.recreateComposition();

    // reposition and rotate article containers
    for (const child of this.children) {
      if (!child || !ContainerUtils.isArticleContainer(child)) {
        continue;
      }

      const articleContainer: ArticleContainer = child as ArticleContainer;
      ArticleContainerUtils.applyTransformationToContainerByUsageProperties(articleContainer);
    }
  }

  /**
   * Refresh container and each children article container.
   */
  public refreshContainer(articleUsageIdsToRefresh: string[] = null): void {
    this.recreateComposition();

    // refresh article containers
    for (const child of this.children) {
      if (!child || !ContainerUtils.isArticleContainer(child)) {
        continue;
      }
      const articleContainer: ArticleContainer = child as ArticleContainer;
      if (!articleUsageIdsToRefresh || articleUsageIdsToRefresh.indexOf(articleContainer.articleGUID) >= 0) {
        articleContainer.refreshContainer();
      }
    }
  }

  protected recreateComposition(): void {
    if (!this.composition) {
      Object3DUtils.disposeAllChildren(this);
      return;
    }
    const articleUsageIds: string[] = ArticleCompositionUtils.articleUsageIds(this.composition);

    const removedChildren: ArticleContainer[] = [];
    const childrenToRefresh: ArticleContainer[] = [];
    const guidsToRemove: string[] = [];
    for (const child of this.children) {
      if (!child || !ContainerUtils.isArticleContainer(child)) {
        continue;
      }

      const articleContainer: ArticleContainer = child as ArticleContainer;
      const artIndex: number = articleUsageIds.indexOf(articleContainer.articleGUID);
      if (artIndex >= 0) {
        // composition already added
        childrenToRefresh.push(articleContainer);
        articleUsageIds.splice(artIndex, 1);
      } else {
        // composition was removed
        this.labelStripArticleManager.processDetachedLabelStrip(articleContainer);
        this.detachCodingFingerArticleService.processDetachedCodingFinger(articleContainer);
        this.articleMoveManager.detachArticleContainerHandler(articleContainer);
        this.projectContentPresenter.articleDetachedHandler(articleContainer);
        removedChildren.push(articleContainer);
        guidsToRemove.push(articleContainer.articleGUID);
      }
    }

    if (guidsToRemove.length > 0) {
      for (const articleContainerToRefresh of childrenToRefresh) {
        articleContainerToRefresh.removeChildGivingUsages(guidsToRemove);
      }
    }

    // remove old children
    for (const removedChild of removedChildren) {
      if (ArticleUtils.isDynamicArticle(removedChild.article)) {
        this.projectModel.removeArticleInfoFromCurrentProject(removedChild.articleGUID);
      }
      this.remove(removedChild);
      Object3DUtils.dispose(removedChild);
      this.imageTextureProcessor.articleContainerDetachedHandler(removedChild);
      this.articlesVisibilityService.refreshArticleTypeList();
      this.carrierBlockManager.articleContainerDetachedHandler(removedChild);
    }

    // add new compositions
    for (const articleUsageGuid of articleUsageIds) {
      const articleContainer: ArticleContainer = this.articleContainerFactory.getNewInstance();
      articleContainer.articleGUID = articleUsageGuid;
      this.add(articleContainer);
    }
    this.sceneManager.renderScene();
  }

  public async attachArticleContainerHandler(data: AttachedArticleContainer): Promise<void> {
    if (data && data.container && this.compositionGuid === data.compositionId) {
      const container: ArticleContainer = data.container;
      await this.internalAttachArticleContainer(container, true, true);
      this.compositeArticleManager.articleContainerAttachedHandler(data);
      this.projectContentPresenter.articleContainerAttachedHandler(data);
      await this.articleLoadingManager.checkLoadedArticlesStatus(data);
      this.articlesVisibilityService.refreshArticleTypeList();
      this.bridgeArticleManager.refreshPinsVisibility(data);
      this.carrierBlockManager.processAttachedArticleContainer(data);
      this.attachCodingFingerArticleService.processAttachedCodingFinger(data);
      this._articleContainerAttachedService.setArticleContainerAttached(data);
      this.sceneManager.renderScene();
    }
  }

  public async attachArticleContainer(container: ArticleContainer, forceArticleRefresh: boolean = true,
                                forceProjectRefresh: boolean = true): Promise<void> {
    await this.internalAttachArticleContainer(container, forceArticleRefresh, forceProjectRefresh);
    this.sceneManager.renderScene();
  }

  private async internalAttachArticleContainer(container: ArticleContainer, forceArticleRefresh: boolean = true,
                                         forceProjectRefresh: boolean = true): Promise<void> {
    this.add(container);
    await this.attachArticleUsageService.attachArticleUsageToProject(container.articleUsage, this.compositionGuid, forceProjectRefresh);
    if (forceArticleRefresh) {
      container.refreshContainer();
    }
  }

  public add(object: Object3D): this {
    this.invalidateBounds();
    return super.add(object);
  }

  public dispose(): void {
    super.dispose();
    Object3DUtils.disposeAllChildren(this);
  }

  protected updateBounds(): void {
    let bounds: Box3;
    for (const articleContainer of this.children) {
      if (ContainerUtils.isArticleContainer(articleContainer)) {
        const transformedBounds: Box3 = Object3DUtils.transformBoundsPoints(articleContainer.bounds, articleContainer.matrix);
        if (!bounds) {
          bounds = transformedBounds;
        }
        bounds.union(transformedBounds);
      }
    }
    if (bounds) {
      this._bounds = bounds;
    }
  }
}
